import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { from, Observable, of, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';
import { CallbackContext, StateValidationResult } from '../callback-context';
import { OidcStorageService } from '../../storage/oidc-storage.service';
import { TokenDecoderService } from '../../utils/token-decoder.service';
import { OpenIdConfiguration } from '../../oidc-security.service';

@Injectable({ providedIn: 'root' })
export class StateValidationCallbackHandlerService {
  constructor(
    private readonly oidcStorageService: OidcStorageService,
    private readonly tokenDecoderService: TokenDecoderService,
    @Inject(DOCUMENT) private readonly document: Document
  ) {}

  // STEP 3 All flows
  validateAndSaveSessionTokens(
    callbackContext: CallbackContext,
    config: OpenIdConfiguration
  ): Observable<CallbackContext> {
    return this.getValidatedStateResult(callbackContext).pipe(
      switchMap((validationResult: StateValidationResult) => {
        callbackContext.validationResult = validationResult;
        if (validationResult.authResponseIsValid) {
          return from(
            Promise.all([
              this.oidcStorageService.setAccessToken(validationResult.accessToken, config.type),
              this.oidcStorageService.setRefreshToken(callbackContext?.authResult?.refresh_token, config.type),
              this.oidcStorageService.setIdToken(callbackContext?.authResult?.id_token, config.type),
            ])
          ).pipe(
            map(() => {
              return callbackContext;
            })
          );
        } else {
          const errorMessage = `authorizedCallback, token(s) validation failed, resetting. Hash: ${this.document.location.hash}`;
          return from(this.oidcStorageService.clear(config.type)).pipe(
            switchMap(() => {
              throw new Error(errorMessage);
            })
          );
        }
      })
    );
  }

  private getValidatedStateResult(callbackContext: CallbackContext): Observable<StateValidationResult> {
    if (!callbackContext || callbackContext.authResult.error) {
      return of(new StateValidationResult('', '', false, {}));
    }

    const toReturn = new StateValidationResult();
    toReturn.idToken = callbackContext.authResult.id_token;
    toReturn.accessToken = callbackContext.authResult.access_token;
    toReturn.authResponseIsValid = true;
    toReturn.decodedIdToken = this.tokenDecoderService.decodeToken(toReturn.idToken);
    return of(toReturn);
  }
}
